import { Component, OnInit, ViewEncapsulation, ViewChild, OnDestroy, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute, RouterOutlet } from '@angular/router';
import { GlobalFunctions, ISQLResult, SnackType, imagePath, imgTypes } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AuthenticationService, IWebAppUser, } from '@services/authentication.service';
import { Account, AccountsService } from '@services/accounts.service';
import { DashboardLayoutComponent, PanelModel } from '@syncfusion/ej2-angular-layouts';
import { IAccount } from '@services/accounts.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { Location } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, finalize, filter, take } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { EditAccountComponent } from '../../../dialogs/edit-account/edit-account.component';
import { AccountManageComponent } from '../account-manage/account-manage.component';
import { IAccountInviteDetail, IAccountInviteResponse, InvitesService } from '@services/invites.service';

/**
 * AccountsListComponent
 */
@Component({
	selector: 'app-accounts-list',
	templateUrl: './accounts-list.component.html',
	styleUrls: ['./accounts-list.component.scss', './accounts-list.component.table.scss'],
	encapsulation: ViewEncapsulation.None
})
export class AccountsListComponent implements OnInit, OnDestroy, AfterViewInit {

	/** Router Outlet */
	@ViewChild('outlet') outlet: RouterOutlet;

	/** My Accounts List */
	public myAccounts: IAccount[]

	/** Displayed Accounts */
	public displayedAccounts: MatTableDataSource<IAccount>;

	/** Page Title */
	public PageTitle: string = "My Accounts"

	/** Selected GUID */
	public selectedGUID: string = ""

	/** Path to Accounts Images */
	public imagePathAccounts(accountGUID?: string): string {
		return imagePath(accountGUID, imgTypes.accounts).toLowerCase()
	}

	/** Current User */
	public currentUser: IWebAppUser

	/** Columns to be displayed in the table */
	// public tableColumns = ["name", "description", "owner", "disabled", "view", "systems"]
	public accountTableColumns = ["Image", "Name", "Description", "Owner", "Address", "Edit"]

	/** DataSource for table */
	public accountDataSource: MatTableDataSource<any>;

	/** MatPaginator */
	@ViewChild(MatPaginator) paginator!: MatPaginator;

	/** MatSort */
	@ViewChild(MatSort) matSort!: MatSort;

	/** Dashboard Layout */
	@ViewChild('default_dashboard') public dashboard: DashboardLayoutComponent;

	/** Current Row */
	public currentRow: any = null; // Variable to hold the current row data

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/**
	 * Default Constructor
	 * @param globalFunctions Global Functions
	 * @param authenticationService Authentication Services
	 * @param router Router
	 * @param location Location
	 * @param siteSubscriptions Site Subscriptions
	 * @param accountsService Account Services
	 * @param dialog MatDialog
	 * @param cdr ChangeDetectorRef
	 */
	constructor(
		private globalFunctions: GlobalFunctions,
		public authenticationService: AuthenticationService,
		private router: Router,
		private location: Location,
		private siteSubscriptions: siteSubscriptions,
		private accountsService: AccountsService,
		public dialog: MatDialog,
		private cdr: ChangeDetectorRef
	) {

	}

	/** isMobileDevice */
	isMobileDevice(): boolean {
		const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
		return /android|iPad|iPhone|iPod|windows phone/i.test(userAgent);
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Page after view init Event Handler
	 */
	ngAfterViewInit() {

		// console.log("constructor")
		// this.router.routeReuseStrategy.shouldReuseRoute = () => false;
		// Validate the user
		this.authenticationService.currentUser$.pipe(
			takeUntil(this.finalise), distinctUntilChanged(),
			filter((currentUser) => !!currentUser)
		).subscribe({
			next: (currentUser) => {
				// Check we have a validated User
				if (!this.currentUser) {
					this.currentUser = <IWebAppUser>currentUser
					//this.getInvites(this.currentUser.Email);
					this.subscribeMyAccounts();

					this.router.events.pipe(
						takeUntil(this.finalise), distinctUntilChanged()
					).subscribe(() => {
						try {
							if (this.outlet && this.outlet.isActivated) {
								const childComponentInstance = this.outlet.component as AccountManageComponent;
								if (childComponentInstance && childComponentInstance.accountsListUpdated) {
									childComponentInstance.accountsListUpdated.pipe(
										takeUntil(this.finalise)
									).subscribe((message: any) => {
										// console.log('Received message from child:', message);
										this.refreshAccountsList()
									});
								}
							}
						}
						catch (error) { console.error(error) }
					});
				}
			}
		})

		this.cdr.detectChanges(); // In case view isn't stabilised
	}

	/**
	 * Refresh My Accounts Subscription
	 */
	refreshAccountsList() {
		let data = {
			"userPublicID": this.currentUser.Sub,
			"showDeleted": true
		}
		this.siteSubscriptions.RefreshMyAccounts(data)
	}

	/**
	 * View the Image Fullsize
	 * @param imageURL
	 */
	viewImage(imageURL: string) {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void { }

	/**
	 * Patch the selected Account
	 * @param account
	 */
	editAccount(account: IAccount) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = { ...account }

		// Display the dialog box
		const dialogRef = this.dialog.open(EditAccountComponent, {
			data: _edit,
		});

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			this.globalFunctions.displaySpinner(true)
			Object.assign(_edit, result);
			this.accountsService.patchAccount$(_edit.GUID, this.currentUser.Sub, _edit).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (result) => {
					Object.assign(account, result);
					this.globalFunctions.DisplaySnackBar({ message: "Account Details Updated", duration: 1000 });
					this.refreshAccountsList();
				},
				error: (error) => { 
					this.globalFunctions.HandleError(error, "myAccounts Error: this.accountsService.patchAccount", false) 
				},
				complete: () => { this.globalFunctions.displaySpinner(false) }
			});
		});

	}

	/**
	 * AddAccount
	 */
	AddAccount() {
		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = <IAccount>{}

		// Display the dialog box
		const dialogRef = this.dialog.open(EditAccountComponent, {
			data: _edit,
		});

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			this.globalFunctions.displaySpinner(true)
			Object.assign(_edit, result);
			this.accountsService.postAccount$(_edit, this.currentUser.Sub).pipe(
				take(1),
				distinctUntilChanged(),
				filter((response) => !!response),
			).subscribe({
				next: (response) => {
					// this.myAccounts.push(<IAccount>response);
					// this.accountDataSource = new MatTableDataSource(this.myAccounts);
					this.globalFunctions.DisplaySnackBar({
						message: "New Account Created",
						type: SnackType.Info,
						duration: 1000
					})
					// Refresh the list to include the new account
					this.refreshAccountsList();
				},
				error: (error) => { 
					this.globalFunctions.HandleError(error, "myAccounts Error: this.accountsService.postAccount", false) 
				},
				complete: () => { this.globalFunctions.displaySpinner(false) }
			});
		});

	}

	/**
	 * Delete the selected Account
	 * @param account
	 */
	deleteAccount(account: IAccount) {

		if (confirm("Delete the Account: " + account.Name + "?")) {
			this.globalFunctions.displaySpinner(true)
			this.accountsService.deleteAccount$(account.GUID, this.currentUser.Sub).pipe(
				take(1),
				distinctUntilChanged(),
				filter((response) => !!response)
			).subscribe({
				next: (response) => {
					var SQLREsult = <ISQLResult>response
					if (SQLREsult.Error === "") {
						// const indexToRemove = this.myAccounts.findIndex(item => item.GUID.toLocaleUpperCase() === account.GUID.toLocaleUpperCase());
						// if (indexToRemove > -1) {
						// 	this.myAccounts.splice(indexToRemove, 1);
						// 	this.accountDataSource = new MatTableDataSource(this.myAccounts);
						// }
						// this.globalFunctions.DisplaySnackBar({
						// 	message: " Account Deleted",
						// 	type: SnackType.Info,
						// 	duration: 1000
						// })
						// Refresh the list to include the new account
						this.refreshAccountsList();
					}
					else {
						this.globalFunctions.DisplaySnackBar({
							message: `${SQLREsult.Error}`,
							type: SnackType.Error,
							duration: 1000
						})
					}
				},
				error: (error) => { this.globalFunctions.HandleError(error, "myAccounts Error: this.accountsService.deleteAccount", false) },
				complete: () => { this.globalFunctions.displaySpinner(false) }
			});
		}
	}

	/**
	 * Select the Row
	 * @param row
	 */
	setCurrentRow(row: any): void {
		this.currentRow = row;
	}

	/**
	 * Get Account Text
	 * @param row
	 * @returns
	 */
	getMyAccountText(row: any): string {
		// Customize the text based on the row data
		return row ? `My Account - ${row.Name}` : 'My Account';
	}

	/**
	 * Selected Account
	 * @param GUID
	 */
	SelectAccount(GUID: string) {
		this.selectedGUID = GUID
		// { queryParams: { guid: GUID } }
		this.router.navigate(["./accounts-management/" + GUID])
	}

	/**
	 * Get My Accounts from site subscriptions
	 */
	subscribeMyAccounts() {
		try {
			this.siteSubscriptions.myAccounts$.pipe(
				takeUntil(this.finalise), distinctUntilChanged()
			).subscribe({
				next: (myAccounts) => {
					if (myAccounts && myAccounts.length > 0) {
						this.myAccounts = myAccounts;
						this.myAccounts = this.siteSubscriptions.normalizeFeatures(this.myAccounts) as Account[];
						this.accountDataSource = new MatTableDataSource(this.myAccounts)
						/** Set the filtered list for mobile view */
						this.displayedAccounts = new MatTableDataSource(this.myAccounts);
						this.displayedAccounts.paginator = this.paginator;
						this.displayedAccounts.sort = this.matSort;
					}
				},
				error: (error) => { this.globalFunctions.HandleError(error, "myAccounts Error: Retrieving Accounts", false) },
				//complete: () => {console.log("myAccounts Error: Retrieving Accounts: Complete");}
			});
		}
		catch (error) { console.error(error) }
	}

	/**
	 * Back Button
	 */
	backClicked() {
		this.location.back();
	}

	/**
	 * Key up event from the search input on HTML
	 * @param event HTMLInputElement
	 */
	applyFilter(value: string) {
		try {
			const filterValue = value.toLowerCase();

			this.accountDataSource.filterPredicate = (data: IAccount, filter: string) => {
				/** Build a string per row to filter and convert to lower case */
				const textToSearch = (data.Name + data.Description).toLowerCase() || '';
				return textToSearch.indexOf(filter) !== -1;
			};

			this.accountDataSource.filter = filterValue.trim().toLowerCase();

			this.displayedAccounts = new MatTableDataSource(this.accountDataSource.filteredData);
			this.displayedAccounts.paginator = this.paginator;
			this.displayedAccounts.sort = this.matSort;

			// Check if there are no matches and display a message
			if (this.accountDataSource.filteredData.length == 0) {
				this.globalFunctions.DisplaySnackBar({
					message: "No matches for '" + filterValue.trim().toLowerCase() + "'",
					type: SnackType.Alert,
					duration: 1000
				})
			}
		}
		catch (error) { this.globalFunctions.HandleError(error, "Error filtering list") }
	}
}
